import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { sort } from 'fast-sort';
import { Observable, firstValueFrom, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { SelectItem } from 'primeng/api';
import { SystemDropDownListI18nViewModel } from '../../common-api/common-api/model/systemDropDownListI18nViewModel';
import { SystemParameterService } from '../../common-api/rt-common-api/system-parameter';
import {
  CacheService,
  MEMORY_CACHE_SERVICE,
} from '../../common/services/cache.service';
import { TranslatableDropdownOption } from '../../core/models';
import { isNullOrEmpty } from '../../core/utils';
import { RetailCodeTableKey } from '../enums';
import { MemoryCache } from '../enums/memory-cache.enum';

/**
 * 共用下拉選單服務
 * @export
 * @class DropdownOptionsService
 */
@Injectable({
  providedIn: 'root',
})
export class RetailDropdownOptionsService {
  constructor(
    @Inject(MEMORY_CACHE_SERVICE) private cacheService: CacheService,
    private commonService: SystemParameterService,
    private translateService: TranslateService,
  ) {}

  /**
   * 取得消金共用下拉選單選項
   *
   * @param {string} [code]
   * @param {boolean} [labelDisplayWithValue] display label in "key - value" format
   * @param {boolean} [useCache] 是否使用 cache
   * @return {*}  {Observable<TranslatableDropdownOption[]>}
   * @memberof RetailDropdownOptionsService
   */
  getRetailDropdownOptions(
    code?: string,
    labelDisplayWithValue?: boolean,
    useCache: boolean = true,
  ): Observable<TranslatableDropdownOption[]> {
    // 使用 cache
    if (useCache) {
      const commonDropdownCache =
        this.cacheService.get<SystemDropDownListI18nViewModel[]>(
          MemoryCache.RETAIL_DROPDOWN_CACHE_KEY,
        ) ?? [];
      const cacheOptions = commonDropdownCache.filter(
        (x) =>
          isNullOrEmpty(code) ||
          (!isNullOrEmpty(code) && x.parentCode === code),
      );
      const observer =
        cacheOptions.length > 0
          ? of(cacheOptions)
          : this.commonService
              .commonSystemParameterCodeTableDropDownListGet(code)
              .pipe(
                tap((results) => {
                  this.saveDropdownCacheData(results);
                }),
              );
      return observer.pipe(
        map((results) => {
          const options = sort(results)
            .asc((x) => x.sortNo)
            .map(
              (x) =>
                new TranslatableDropdownOption(
                  this.translateService,
                  x,
                  labelDisplayWithValue ?? false,
                ),
            );
          return options;
        }),
      );
    }

    const observer = this.commonService
      .commonSystemParameterCodeTableDropDownListGet(code)
      .pipe(
        map((results) => {
          const options = sort(results)
            .asc((x) => x.sortNo)
            .map(
              (x) =>
                new TranslatableDropdownOption(
                  this.translateService,
                  x,
                  labelDisplayWithValue ?? false,
                ),
            );
          return options;
        }),
      );
    return observer;
  }

  getRetailDealerRatingDropdownOptions(
    code?: string,
    userNo?: string,
    labelDisplayWithValue?: boolean,
    useCache: boolean = true,
  ): Observable<TranslatableDropdownOption[]> {
    // 使用 cache
    if (useCache) {
      const commonDropdownCache =
        this.cacheService.get<SystemDropDownListI18nViewModel[]>(
          MemoryCache.RETAIL_DROPDOWN_CACHE_KEY,
        ) ?? [];
      const cacheOptions = commonDropdownCache.filter(
        (x) =>
          isNullOrEmpty(code) ||
          (!isNullOrEmpty(code) && x.parentCode === code),
      );
      const observer =
        cacheOptions.length > 0
          ? of(cacheOptions)
          : this.commonService
              .commonSystemParameterCodeTableGetDealerRatingDropDownListByUserNoGet(
                code,
                userNo,
              )
              .pipe(
                tap((results) => {
                  this.saveDropdownCacheData(results);
                }),
              );
      return observer.pipe(
        map((results) => {
          const options = sort(results)
            .asc((x) => x.sortNo)
            .map(
              (x) =>
                new TranslatableDropdownOption(
                  this.translateService,
                  x,
                  labelDisplayWithValue ?? false,
                ),
            );
          return options;
        }),
      );
    }

    const observer = this.commonService
      .commonSystemParameterCodeTableDropDownListGet(code)
      .pipe(
        map((results) => {
          const options = sort(results)
            .asc((x) => x.sortNo)
            .map(
              (x) =>
                new TranslatableDropdownOption(
                  this.translateService,
                  x,
                  labelDisplayWithValue ?? false,
                ),
            );
          return options;
        }),
      );
    return observer;
  }

  /**
   * 取得選項並設定至selectList
   * @param code
   * @param useSelectOptions pageData.selectList
   * @param optionListKey pageData.selectList內的選單的key name (e.g. pageData.selectList.xxxx 因此傳xxxx)
   * @param labelDisplayWithValue
   * @param useCache
   */
  async getAndSetDropdownOptions(
    code: string,
    useSelectOptions: Record<
      string,
      TranslatableDropdownOption[] | SelectItem[]
    >,
    optionListKey: string,
    labelDisplayWithValue?: boolean,
    useCache: boolean = true,
  ) {
    const resultOptions: TranslatableDropdownOption[] = await new Promise(
      (resolve) => {
        this.getRetailDropdownOptions(
          code,
          labelDisplayWithValue,
          useCache,
        ).subscribe({
          next: (result) => resolve(result),
          error: (err) => err,
        });
      },
    );
    useSelectOptions[optionListKey] = await resultOptions;
  }
  /**
   * 批次撈取並設定下拉選單
   * @param codeList
   * @param useSelectOptions pageData.selectList
   * @param optionListKeyList 欲設定選單key,如有傳入長度需與codeList相同
   * @param labelDisplayWithValue
   * @param useCache
   */
  batchGetAndSetDropdownOptions(
    codeList: string[],
    useSelectOptions: Record<
      string,
      TranslatableDropdownOption[] | SelectItem[]
    >,
    optionListKeyList?: string[],
    labelDisplayWithValue?: boolean,
    useCache: boolean = true,
  ) {
    codeList.forEach(async (code, index) => {
      const resultOptions: TranslatableDropdownOption[] = await new Promise(
        (resolve) => {
          this.getRetailDropdownOptions(
            code,
            labelDisplayWithValue,
            useCache,
          ).subscribe({
            next: (result) => resolve(result),
            error: (err) => err,
          });
        },
      );
      useSelectOptions[optionListKeyList?.[index] ?? index] =
        await resultOptions;
    });
  }
  /**
   * 批次撈取並設定下拉選單（使用Promise確定會全部傳回)
   * @param codeList
   * @param useSelectOptions pageData.selectList
   * @param optionListKeyList 欲設定選單key,如有傳入長度需與codeList相同
   * @param labelDisplayWithValue
   * @param useCache
   */
  batchGetAndSetDropdownOptionsPromise(
    codeList: string[],
    useSelectOptions: Record<
      string,
      TranslatableDropdownOption[] | SelectItem[]
    >,
    optionListKeyList?: string[],
    labelDisplayWithValue?: boolean,
    useCache: boolean = true,
  ) {
    return Promise.all(
      codeList.map(async (code, index) => {
        const resultOptions: TranslatableDropdownOption[] = await new Promise(
          (resolve) => {
            this.getRetailDropdownOptions(
              code,
              labelDisplayWithValue,
              useCache,
            ).subscribe({
              next: (result) => resolve(result),
              error: (err) => err,
            });
          },
        );
        useSelectOptions[optionListKeyList?.[index] ?? index] = resultOptions;
        return resultOptions;
      }),
    );
  }

  /**
   * 批次撈取並設定下拉選單(使用map對應)
   * @param optionListKeyWithcodeList 下拉選單keyName對應 選項代號 e.g. {csmrIndivGenderOptions:RetailCodeTableKey.A018}
   * @param useSelectOptions pageData.selectList
   * @param optionListKeyList 欲設定選單key,如有傳入長度需與codeList相同
   * @param labelDisplayWithValue
   * @param useCache
   */
  batchGetAndSetDropdownOptionsByMap(
    optionListKeyWithcodeList: Record<string, RetailCodeTableKey>,
    useSelectOptions: Record<
      string,
      TranslatableDropdownOption[] | SelectItem[]
    >,
    labelDisplayWithValue?: boolean,
    useCache: boolean = true,
  ) {
    const optionListKeys = Object.keys(optionListKeyWithcodeList);
    let promises = [] as Promise<TranslatableDropdownOption[]>[];
    Object.values(optionListKeyWithcodeList).forEach(async (code, index) => {
      promises.push(
        new Promise(async (resolve) => {
          const resultOptions: TranslatableDropdownOption[] =
            await firstValueFrom(
              this.getRetailDropdownOptions(
                code,
                labelDisplayWithValue,
                useCache,
              ),
            );
          useSelectOptions[optionListKeys[index]] = resultOptions;
          resolve(resultOptions);
        }),
      );
    });
    return Promise.all(promises);
  }
  private saveDropdownCacheData(
    options: SystemDropDownListI18nViewModel[],
  ): void {
    const commonDropdownCache =
      this.cacheService.get<SystemDropDownListI18nViewModel[]>(
        MemoryCache.RETAIL_DROPDOWN_CACHE_KEY,
      ) ?? [];
    options.forEach((option) => {
      const idx = commonDropdownCache.findIndex(
        (x) => x.type === option.type && x.code === option.code,
      );
      if (idx > -1)
        // 更新 cache
        commonDropdownCache[idx] = option;
      // 新增至 cache
      else commonDropdownCache.push(option);

      this.cacheService.add(
        MemoryCache.RETAIL_DROPDOWN_CACHE_KEY,
        commonDropdownCache,
      );
    });
  }
}
